import { from, Observable, of } from "rxjs";
import { ofType } from 'redux-observable';
import {
  catchError,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { CreateErrorNotice, CreateSuccessNotice } from '../actions/toaster.action';
import OrganizationService from '../../shared/services/organization.service';
import {
  CreateNewOrg, CreateNewOrgFailed, CreateNewOrgSucceeded,
  EditOrganization,
  EditOrganizationFailed,
  EditOrganizationSucceeded,
  LoadOrg,
  LoadOrgChildren,
  LoadOrgChildrenFailed,
  LoadOrgChildrenSuccess,
  LoadOrgFailed, LoadOrgHierarchy, LoadOrgHierarchyFailed, LoadOrgHierarchySuccess,
  LoadOrgList, LoadOrgListFailed, LoadOrgListSucceeded,
  LoadOrgSubscriptionStatus,
  LoadOrgSubscriptionStatusFailed,
  LoadOrgSubscriptionStatusSuccess,
  LoadOrgSuccess,
  LoadSelectedOrg,
  LoadSelectedOrgFailed,
  LoadSelectedOrgSuccess,
  OrgHierarchyAction,
  OrgHierarchyActions,
  SelectActiveNode,
  SelectOrg,
} from '../actions/orgHierarchy.action';
import { ServerErrorMessages } from '../../shared/utils/server-error-utils';
import { RootState } from "../state";

const loadOrg = (action$: Observable<OrgHierarchyAction>) => action$.pipe(
  ofType<LoadOrg>(OrgHierarchyActions.LoadOrg),
  switchMap(({ payload }) => from(OrganizationService.getOrgHierarchy(payload)).pipe(
    mergeMap((org) => of(
      new LoadOrgSuccess(org),
      ),
    ),
    catchError((error) => (
      of(
        new LoadOrgFailed(error),
        new CreateErrorNotice('Error', 'Failed to load an org'),
      )
    )),
  )),
);

const loadOrgHierarchy = (action$: Observable<OrgHierarchyAction>) => action$.pipe(
  ofType<LoadOrgHierarchy>(OrgHierarchyActions.LoadOrgHierarchy),
  switchMap(({ payload }) => from(OrganizationService.getOrgHierarchy(payload)).pipe(
    mergeMap((org) => of(
        new LoadOrgHierarchySuccess(org),
      ),
    ),
    catchError((error) => (
      of(
        new LoadOrgHierarchyFailed(error),
        new CreateErrorNotice('Error', 'Failed to load an org tree'),
      )
    )),
  )),
);

const loadOrgSubscriptionStatus = (action$: Observable<OrgHierarchyAction>) => action$.pipe(
  ofType<LoadOrgSubscriptionStatus>(OrgHierarchyActions.LoadOrgSubscriptionStatus),
  switchMap(({ payload }) => from(OrganizationService.getOrg(payload, true)).pipe(
    mergeMap((org) => of(
      new LoadOrgSubscriptionStatusSuccess(org),
      ),
    ),
    catchError((error) => (
      of(
        new LoadOrgSubscriptionStatusFailed(error),
        new CreateErrorNotice('Error', 'Failed to load an org'),
      )
    )),
  )),
);

const loadOrgChildren = (action$: Observable<OrgHierarchyAction>) => action$.pipe(
  ofType<LoadOrgChildren>(OrgHierarchyActions.LoadOrgChildren),
  switchMap(({ payload }) => from(OrganizationService.getOrgChildren(payload)).pipe(
    switchMap((children) => of(
      new LoadOrgChildrenSuccess({
        id: payload,
        children
      }),
      ),
    ),
    catchError((error) => (
      of(
        new LoadOrgChildrenFailed(error),
        new CreateErrorNotice('Error', 'Failed to load org children'),
      )
    )),
  )),
);

const loadSelectedOrg = (action$: Observable<OrgHierarchyAction>, $state: Observable<RootState>) => action$.pipe(
  ofType<LoadSelectedOrg>(OrgHierarchyActions.LoadSelectedOrg),
  withLatestFrom($state),
  switchMap(([{ payload }, state]) => from(OrganizationService.getOrg(payload, true, true)).pipe(
    switchMap((org) => of(
      new SelectActiveNode(org),
      new SelectOrg(org),
      new LoadSelectedOrgSuccess(org)
      ),
    ),
    catchError((error) => (
      of(
        new LoadSelectedOrgFailed(error),
        new LoadSelectedOrg(state?.orgHierarchy?.rootOrg?._id),
        new CreateErrorNotice('Error', 'Failed to load selected org'),
      )
    )),
  )),
);

const updateOrgEpic = ($action: Observable<OrgHierarchyAction>) => $action.pipe(
  ofType<EditOrganization>(OrgHierarchyActions.EditOrganization),
  switchMap((action) => from(OrganizationService.editOrg(action.payload.orgId, action.payload.updates)).pipe(
    mergeMap(() => of(
      new EditOrganizationSucceeded(),
      new LoadOrg(action.payload.orgId),
      new LoadSelectedOrg(action.payload.orgId),
      new CreateSuccessNotice('Success', 'User information updated'),
    )),
    catchError((error: Error) => of(
      new EditOrganizationFailed(ServerErrorMessages[error.message] || 'Failed to update user'),
      new CreateErrorNotice('Error', 'Failed to update user'),
      ),
    )),
  ));


const loadOrgList = (action$: Observable<OrgHierarchyAction>) => action$.pipe(
  ofType<LoadOrgList>(OrgHierarchyActions.LoadOrgList),
  switchMap(() => from(OrganizationService.getOrgList()).pipe(
    mergeMap((orgs) => of(
      new LoadOrgListSucceeded(orgs),
      ),
    ),
    catchError((error) => (
      of(
        new LoadOrgListFailed(error),
        new CreateErrorNotice('Error', 'Failed to load an org'),
      )
    )),
  )),
);

const createOrg = (action$: Observable<OrgHierarchyAction>) => action$.pipe(
  ofType<CreateNewOrg>(OrgHierarchyActions.CreateNewOrg),
  switchMap(({ payload }) => from(OrganizationService.createOrg(payload.parentId, payload.name, payload.adminEmail, payload.tags, {}, undefined)).pipe(
    mergeMap((org) => of(
        new CreateNewOrgSucceeded(org),
        new LoadOrgChildren(payload.parentId)
      ),
    ),
    catchError((error) => (
      of(
        new CreateNewOrgFailed(ServerErrorMessages[error.message] || 'Failed to create an org'),
        new CreateErrorNotice('Error', 'Failed to load an org'),
      )
    )),
  )),
);

const OrgHierarchyEpics = [
  loadOrg,
  loadOrgHierarchy,
  loadOrgSubscriptionStatus,
  loadOrgChildren,
  loadSelectedOrg,
  updateOrgEpic,
  loadOrgList,
  createOrg
];

export default OrgHierarchyEpics;
